// src/dbservice/repo.js
import { dbs } from './dbs'; // Added ./ here
import { db as firestore } from '../firebase';

import { collection, getDocs, query, where, orderBy, onSnapshot} from 'firebase/firestore';


export class Repository  {

  async fetchById(collectionName, id) {
    // 1. Attempt to get from local Dexie cache first
    const cachedItem = await dbs[collectionName].get(id);
    
    if (cachedItem) {
      console.info(`[Repository] Found ${id} in local cache.`);
      return cachedItem;
    }

    // 2. If not in cache, fetch from Firestore
    console.info(`[Repository] ${id} not in cache, fetching from Firestore.`);
    try {
      const { doc, getDoc, collection } = await import('firebase/firestore');
      const docRef = doc(firestore, collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        
        // 3. Optional: Sync this single item back to Dexie for future lookups
        await dbs[collectionName].put(data);
        
        return data;
      } else {
        console.warn(`[Repository] Document ${id} not found in Firestore.`);
        return null;
      }
    } catch (error) {
      console.error(`[Repository] Error fetching document ${id}:`, error);
      throw error;
    }
  }

  async fetchData(collectionName, filters = [], sortRules =[],ttlMinutes = 60) {
    
    const now = Date.now();
    // Unique key for metadata tracking
    const cacheKey = filters.length > 0 
      ? filters.map(f => `${f.field}_${f.value}`).join('|') 
      : 'all';

    // 1. Check local cache
    // Use a conditional Dexie query
    const cached = filters.length > 0 
      ? await dbs[collectionName].where(filters[0].field).equals(filters[0].value).toArray()
      : await dbs[collectionName].toArray();
    
    const syncInfo = await dbs.syncMetadata.get(cacheKey);
    
    // Consider it stale if no syncInfo exists OR time has passed
    const isStale = !syncInfo || (now - syncInfo.lastUpdated > ttlMinutes * 60000);

    // Return if fresh (even if empty, to avoid redundant network calls)
    if (syncInfo && !isStale) {
        console.info(`[Repository] Returning ${cached.length} items from cache for ${collectionName} with filters:`, filters);
      return cached;
    }

    // 2. Fetch from Firebase
    const queryParts = filters.map(f => where(f.field, '==', f.value));
    
    const orderParts = sortRules.map(s => {
  const direction = s.order.startsWith('desc') ? 'desc' : 'asc';
  return orderBy(s.field, direction);
});
    
    const firestoreQuery = query(
      collection(firestore, collectionName),
      ...queryParts,
      ...orderParts
    );
    
    var  snapshot = null;
    try{
      snapshot = await getDocs(firestoreQuery);
      console.info(`[Repository] Fetched ${snapshot.size} items from Firestore for ${collectionName} with filters:`, filters);
    }catch(error){
       
       throw error;
    }

    const remoteData = snapshot ? snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) : [];
    // 3. Save to Dexie and metadata
    await dbs[collectionName].bulkPut(remoteData);
    await dbs.syncMetadata.put({ 
      id: cacheKey, 
      lastUpdated: now,
      collection: collectionName 
    });

    return remoteData;
  }

  async  subscribeToUpdates(collectionName, filters = [], callback) {
    // 1. Build the dynamic query based on filters
    const queryParts = filters.map(f => where(f.field, '==', f.value));
    const q = query(collection(firestore, collectionName), ...queryParts);
    
    // 2. Return the unsubscribe function so the component/service can stop it
    return onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") {
          dbs[collectionName].delete(change.doc.id);
        } else {
          dbs[collectionName].put({ id: change.doc.id, ...change.doc.data() });
        }
      });
      
      // 3. Trigger the callback to let the UI know data changed
      if (callback) callback();
    });
  }

  async deleteData(collectionName, id) {
    try {
      // 1. Import necessary Firestore functions
      const { doc, deleteDoc } = await import('firebase/firestore');

      // 2. Delete from Firestore
      const docRef = doc(firestore, collectionName, id);
      await deleteDoc(docRef);
      console.info(`[Repository] Document ${id} deleted from Firestore.`);

      // 3. Delete from Local Dexie Cache
      await dbs[collectionName].delete(id);
      console.info(`[Repository] Document ${id} deleted from local cache.`);

      return { success: true };
    } catch (error) {
      console.error(`[Repository] Error deleting document ${id}:`, error);
      throw error;
    }
  }

  async removeFromCache(collectionName, id) {
    // Utility to specifically clean up cache without affecting Firestore
    await dbs[collectionName].delete(id);
    console.info(`[Repository] Removed ${id} from local cache only.`);
  }
};